import { Button, Stack, Typography, Box, useTheme } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'

type HowCredentialsWorkDialogProps = {
  overlay: OverlayState
}

const exampleItems = [
  // Root
  {
    x: 350,
    y: 30,
    w: 160,
    h: 60,
    label: 'Root Org',
    cred: 'Credential A',
    type: 'explicit',
  },
  // Level 1
  {
    x: 150,
    y: 140,
    w: 160,
    h: 54,
    label: 'Child Org 1',
    cred: 'Credential B',
    type: 'explicit',
  },
  {
    x: 550,
    y: 140,
    w: 160,
    h: 54,
    label: 'Child Org 2',
    cred: 'Uses A',
    type: 'inherit',
  },
  // Level 2 (inherits)
  {
    x: 50,
    y: 240,
    w: 150,
    h: 54,
    label: 'Grandchild 1',
    cred: 'Uses B',
    type: 'inherit',
  },
  {
    x: 250,
    y: 240,
    w: 150,
    h: 54,
    label: 'Grandchild 2',
    cred: 'Uses B',
    type: 'inherit',
  },
  {
    x: 450,
    y: 240,
    w: 150,
    h: 54,
    label: 'Grandchild 3',
    cred: 'Uses A',
    type: 'inherit',
  },
  {
    x: 650,
    y: 240,
    w: 150,
    h: 54,
    label: 'Grandchild 4',
    cred: 'Uses A',
    type: 'inherit',
  },
]

export default function HowCredentialsWorkDialog({
  overlay,
}: HowCredentialsWorkDialogProps) {
  const theme = useTheme()

  return (
    <Dialog title="How Credentials Work" maxWidth="md" {...overlay}>
      <DialogContent>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="body1">
              Credentials let Pixydocs securely connect to external systems
              (EHRs, PMSs, APIs, etc.) while processing documents. They are
              resolved based on your organization tree so you rarely need to
              duplicate them.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Key concepts:
            </Typography>
            <Stack component="ul" sx={{ pl: 3 }} spacing={1}>
              <Typography component="li" variant="body2">
                <strong>Per organization:</strong> A credential is attached to a
                specific organization.
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Inherited upward:</strong> Credentials are discovered by
                walking <em>up</em> the tree. A child org automatically uses the
                nearest credential defined on itself, its parent, or any
                ancestor.
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Document resolution:</strong> When a document is
                processed we start at the document&apos;s assigned organization
                and use the first credential found while moving toward the root.
              </Typography>
              <Typography component="li" variant="body2">
                <strong>One credential scenario:</strong> If you only ever need
                one credential for an integration, attach it to the top (root)
                organization. All descendants inherit it automatically.
              </Typography>
            </Stack>
          </Stack>

          {/* Visual Inheritance Diagram */}
          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Inheritance Example
            </Typography>
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                bgcolor: 'background.default',
                overflowX: 'auto',
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 860 300"
                role="img"
                aria-label="Credential inheritance diagram"
                sx={{
                  width: '100%',
                  height: 'auto',
                  '& text': { fontFamily: 'inherit' },
                }}
              >
                <defs>
                  <linearGradient id="rootGrad" x1="0" x2="0" y1="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={theme.palette.primary.light}
                      stopOpacity={0.9}
                    />
                    <stop
                      offset="100%"
                      stopColor={theme.palette.primary.main}
                      stopOpacity={0.95}
                    />
                  </linearGradient>
                  <filter
                    id="shadow"
                    x="-20%"
                    y="-20%"
                    width="140%"
                    height="140%"
                  >
                    <feDropShadow
                      dx="0"
                      dy="2"
                      stdDeviation="4"
                      floodOpacity="0.25"
                    />
                  </filter>
                </defs>

                {/* Connecting curves functionally grouped */}
                <g
                  stroke={theme.palette.divider}
                  strokeWidth={2}
                  strokeLinecap="round"
                  fill="none"
                  strokeLinejoin="round"
                >
                  {/* Root to children */}
                  <path d="M430 90 C430 110 230 110 230 140" />
                  <path d="M430 90 C430 110 630 110 630 140" />
                  {/* Child 1 to grands */}
                  <path d="M230 200 C230 220 120 220 120 240" />
                  <path d="M230 200 C230 220 340 220 340 240" />
                  {/* Child 2 to grands */}
                  <path d="M630 200 C630 220 520 220 520 240" />
                  <path d="M630 200 C630 220 740 220 740 240" />
                </g>

                {/* Node helper (explicit credential vs inherit) */}
                {exampleItems.map((n, i) => {
                  const isRoot = i === 0
                  const showCred = !!n.cred
                  const explicit = n.type === 'explicit'
                  const inherit = n.type === 'inherit'
                  const fill = isRoot
                    ? 'url(#rootGrad)'
                    : explicit
                    ? theme.palette.background.paper
                    : 'none'
                  const stroke = explicit
                    ? theme.palette.primary.main
                    : inherit
                    ? theme.palette.text.disabled
                    : theme.palette.divider
                  return (
                    <g key={i} filter={isRoot ? 'url(#shadow)' : undefined}>
                      <rect
                        x={n.x}
                        y={n.y}
                        width={n.w}
                        height={n.h}
                        rx={12}
                        fill={fill}
                        stroke={stroke}
                        strokeWidth={explicit || isRoot ? 2 : 1.5}
                        opacity={inherit ? 0.9 : 1}
                      />
                      <text
                        x={n.x + n.w / 2}
                        y={n.y + (inherit ? 20 : 22)}
                        fontSize={13}
                        textAnchor="middle"
                        fill={
                          inherit
                            ? theme.palette.text.secondary
                            : theme.palette.text.primary
                        }
                      >
                        {n.label}
                      </text>
                      {showCred && (
                        <g>
                          {(() => {
                            const pillWidth = inherit ? n.w - 28 : 104
                            const pillX = inherit
                              ? n.x + (n.w - pillWidth) / 2
                              : n.x + n.w / 2 - pillWidth / 2
                            const pillY = inherit
                              ? n.y + n.h - 28
                              : n.y + n.h - 26
                            return (
                              <rect
                                x={pillX}
                                y={pillY}
                                width={pillWidth}
                                height={22}
                                rx={11}
                                fill={
                                  explicit
                                    ? theme.palette.mode === 'dark'
                                      ? theme.palette.primary.dark
                                      : theme.palette.primary.light
                                    : 'transparent'
                                }
                                stroke={
                                  explicit
                                    ? theme.palette.primary.main
                                    : theme.palette.text.disabled
                                }
                                strokeWidth={1}
                              />
                            )
                          })()}
                          <text
                            x={n.x + n.w / 2}
                            y={n.y + n.h - (inherit ? 14 : 12)}
                            fontSize={11}
                            textAnchor="middle"
                            fill={
                              explicit
                                ? theme.palette.getContrastText(
                                    theme.palette.primary.main,
                                  )
                                : theme.palette.text.secondary
                            }
                          >
                            {n.cred}
                          </text>
                        </g>
                      )}
                    </g>
                  )
                })}

                {/* Legend */}
                <g fontSize={11}>
                  <rect
                    x={20}
                    y={20}
                    width={12}
                    height={12}
                    rx={3}
                    fill={theme.palette.primary.main}
                  />
                  <text x={38} y={30} fill={theme.palette.text.secondary}>
                    Credential Defined
                  </text>
                  <rect
                    x={20}
                    y={40}
                    width={12}
                    height={12}
                    rx={3}
                    fill="none"
                    stroke={theme.palette.text.disabled}
                  />
                  <text x={38} y={50} fill={theme.palette.text.secondary}>
                    Inherited (nearest ancestor)
                  </text>
                </g>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Grandchildren use the closest credential found while traversing
                upward.
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogFooter>
        <Button onClick={overlay.close} variant="text">
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
